import { FC, useState } from 'react';
import { Box, Paper, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import SchoolIcon from '@mui/icons-material/School';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MapIcon from '@mui/icons-material/Map';
import ExtensionIcon from '@mui/icons-material/Extension';
import SettingsIcon from '@mui/icons-material/Settings';
import LinkIcon from '@mui/icons-material/Link';
import { ContentManager } from './ContentManager';
import LinkageViewer from './LinkageViewer';

interface BrowserViewProps {
  currentEnvironment: string;
  contentKey: number;
}

interface FolderItem {
  id: string;
  label: string;
  icon: JSX.Element;
  folder: string;
}

const FOLDER_ITEMS: FolderItem[] = [
  { id: 'categories', label: 'Categories', icon: <CategoryIcon />, folder: 'categories' },
  { id: 'courses', label: 'Courses', icon: <SchoolIcon />, folder: 'courses' },
  { id: 'competency_frameworks', label: 'Competency Frameworks', icon: <AccountTreeIcon />, folder: 'competency_frameworks' },
  { id: 'learning_path_templates', label: 'Learning Path Templates', icon: <MapIcon />, folder: 'learning_path_templates' },
  { id: 'cmi5_activities', label: 'CMI5 Activities', icon: <ExtensionIcon />, folder: 'cmi5_activities' },
  { id: 'linkages', label: 'Linkages', icon: <LinkIcon />, folder: 'linkages' },
  { id: 'root', label: 'Configuration Files', icon: <SettingsIcon />, folder: 'root' },
];

export const BrowserView: FC<BrowserViewProps> = ({ currentEnvironment, contentKey }) => {
  const [selectedFolder, setSelectedFolder] = useState<string>('categories');

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* Left sidebar - Folder list */}
      <Paper
        elevation={2}
        sx={{
          width: 280,
          minWidth: 280,
          overflow: 'auto',
          borderRight: 1,
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <List sx={{ py: 2 }}>
          {FOLDER_ITEMS.map((item) => (
            <ListItemButton
              key={item.id}
              selected={selectedFolder === item.id}
              onClick={() => setSelectedFolder(item.id)}
              sx={{ mx: 2, borderRadius: 1 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Right content area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {(() => {
          const item = FOLDER_ITEMS.find(f => f.id === selectedFolder);
          if (!item) return null;

          // Show LinkageViewer for linkages section
          if (item.id === 'linkages') {
            return (
              <LinkageViewer
                key={`${contentKey}-linkages`}
                currentEnvironment={currentEnvironment}
              />
            );
          }

          // Show ContentManager for all other folders
          return (
            <ContentManager
              key={`${contentKey}-${item.folder}`}
              folder={item.folder}
              currentEnvironment={currentEnvironment}
            />
          );
        })()}
      </Box>
    </Box>
  );
};
